/**
 * Mis consumos (`/consumos`).
 *
 * Portado de `PortalCliente::tickets()` + `api_tickets`: los tickets de crédito
 * del cliente (`credito_venta`) con su producto, cantidad, placa, conductor y
 * quién lo atendió.
 */

import { Download, Fuel, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { exportarCsv } from '@/lib/export';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateTimeValue, formatNumber } from '@/lib/utils';

import { usePortalConsumos } from '../portal.hooks';

/** Primer día del mes en curso (`AAAA-MM-DD`). */
function inicioDeMes(): string {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

export function PortalConsumosPage() {
  useDocumentTitle('Portal · Consumos');
  const [parametros, setParametros] = useSearchParams();

  const filtros = useMemo(
    () => ({
      desde: parametros.get('desde') ?? inicioDeMes(),
      hasta: parametros.get('hasta') ?? new Date().toISOString().slice(0, 10),
    }),
    [parametros],
  );

  const consulta = usePortalConsumos(filtros);

  const [busqueda, setBusqueda] = useState('');

  const consumos = useMemo(() => consulta.data?.consumos ?? [], [consulta.data]);

  // Búsqueda en el navegador sobre el rango ya pedido a la API.
  const filtrados = useMemo(
    () =>
      consumos.filter((item) =>
        coincideBusqueda(
          [
            item.producto,
            item.placa,
            item.conductor,
            item.vale,
            item.atendido_por,
            formatDateTimeValue(item.fecha),
            formatCurrency(item.total),
            item.liquidado ? 'liquidado' : 'sin liquidar',
          ],
          busqueda,
        ),
      ),
    [consumos, busqueda],
  );

  const paginacion = usePaginacion(filtrados, {
    clave: `${busqueda}|${filtros.desde}|${filtros.hasta}`,
  });

  const totalFiltrado = filtrados.reduce((suma, item) => suma + item.total, 0);
  const galonesFiltrados = filtrados.reduce((suma, item) => suma + item.cantidad, 0);

  const cambiar = (campo: 'desde' | 'hasta', valor: string) => {
    const siguiente = new URLSearchParams(parametros);
    siguiente.set(campo, valor);
    setParametros(siguiente, { replace: true });
  };

  const exportar = () => {
    const filas = filtrados.map((item) => [
      formatDateTimeValue(item.fecha),
      item.producto,
      item.cantidad.toFixed(2),
      item.precio.toFixed(2),
      item.total.toFixed(2),
      item.placa ?? '',
      item.conductor ?? '',
      item.vale ?? '',
      item.atendido_por ?? '',
    ]);

    exportarCsv(
      'consumos-portal',
      ['Fecha', 'Producto', 'Cantidad', 'Precio', 'Total', 'Placa', 'Conductor', 'Vale', 'Atendido por'],
      filas,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={Fuel}
          titulo="Mis consumos"
          descripcion="Detalle de tus cargas a crédito, con placa, conductor y quién te atendió."
        />
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          disabled={filtrados.length === 0}
          onClick={exportar}
        >
          Descargar CSV
        </Button>
      </div>

      <SectionCard title="Rango de fechas" description="Por defecto, el mes en curso.">
        <div className="grid gap-3 sm:grid-cols-2 xl:max-w-xl">
          <Input
            label="Desde"
            type="date"
            value={filtros.desde}
            onChange={(evento) => cambiar('desde', evento.target.value)}
          />
          <Input
            label="Hasta"
            type="date"
            value={filtros.hasta}
            onChange={(evento) => cambiar('hasta', evento.target.value)}
          />
        </div>
      </SectionCard>

      {consulta.error ? (
        <Alert variant="error">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'No se pudieron cargar tus consumos.'}
        </Alert>
      ) : null}

      <SectionCard
        title={`Consumos (${consulta.data?.total ?? 0})`}
        description={
          consulta.data
            ? `${busqueda.trim() ? `${filtrados.length} de ${consumos.length} · ` : `${consumos.length} consumos · `}${formatNumber(galonesFiltrados)} gal · total ${formatCurrency(totalFiltrado)}`
            : 'Cargando…'
        }
        action={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-3.5', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
            disabled={consulta.isFetching}
          >
            Actualizar
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busqueda}
            onChange={setBusqueda}
            ariaLabel="Buscar consumos"
            placeholder="Buscar producto, placa, conductor o vale…"
          />

          {busqueda.trim() ? (
            <div className="flex flex-wrap items-center gap-2 rounded-control border border-brand-100 bg-brand-50/60 px-3 py-2 text-xs text-ink-600">
              <span>
                <strong className="font-semibold text-ink-800">{filtrados.length}</strong> de{' '}
                {consumos.length} consumos coinciden con{' '}
                <strong className="font-semibold text-ink-800">“{busqueda.trim()}”</strong>
              </span>
            </div>
          ) : null}

          {consulta.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : consumos.length === 0 ? (
            <EstadoVacio
              icon={Fuel}
              titulo="Sin consumos en esas fechas"
              detalle="No registramos cargas a tu nombre en el rango elegido."
            />
          ) : filtrados.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos consumos para “${busqueda.trim()}”. Prueba con otro producto o placa.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusqueda('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Fecha</th>
                  <th className="py-2 font-semibold">Producto</th>
                  <th className="hidden py-2 text-right font-semibold sm:table-cell">Cantidad</th>
                  <th className="hidden py-2 text-right font-semibold sm:table-cell">Precio</th>
                  <th className="py-2 text-right font-semibold">Total</th>
                  <th className="hidden py-2 font-semibold xl:table-cell">Placa / conductor</th>
                  <th className="hidden py-2 font-semibold xl:table-cell">Atendido por</th>
                </tr>
              </thead>
              <tbody>
                {paginacion.visibles.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2 whitespace-nowrap text-ink-600">
                      {formatDateTimeValue(item.fecha)}
                    </td>
                    <td className="py-2">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-ink-800">{item.producto}</span>
                        {item.vale ? <Badge variant="outline">vale</Badge> : null}
                        {item.liquidado ? <Badge variant="success">liquidado</Badge> : null}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-ink-500 sm:hidden">
                        {formatNumber(item.cantidad)} gal
                      </span>
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-ink-600 sm:table-cell">
                      {formatNumber(item.cantidad)}
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-ink-600 sm:table-cell">
                      {formatCurrency(item.precio)}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums text-ink-900">
                      {formatCurrency(item.total)}
                    </td>
                    <td className="hidden py-2 text-ink-600 xl:table-cell">
                      {item.placa ?? '—'}
                      {item.conductor ? ` · ${item.conductor}` : ''}
                    </td>
                    <td className="hidden py-2 text-ink-600 xl:table-cell">
                      {item.atendido_por ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador {...paginacion} etiqueta="consumos" className="-mx-5 -mb-5 mt-3 rounded-b-card" />
      </SectionCard>
    </div>
  );
}
